import {useState, useEffect, useMemo} from 'react';
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import Table from '../table';
import ActionBar from '../actionBar';
import {Button, Badge} from 'react-bootstrap';
import {dateFormatter} from '../dateFormatter';
import {useAtomValue} from 'jotai';
import { pageSizeRecoil, showDeleted } from '../recoil';
import {usePortalContext} from '../../lib/context/portalContext';
import {useCustomerContext} from '../../lib/context/customerContext';
import Portal from '../portal';
import EditPolicy from './editPolicy';
import CreatePolicy from './createPolicy';
import MinimumPolicy from './minimumPolicy';
import {Link} from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faXmark, faUsersRectangle, faTriangleExclamation} from '@fortawesome/pro-light-svg-icons';
import { faCircle} from '@fortawesome/pro-solid-svg-icons';
import variables from '../../styles/_variables.module.scss';
import {useSearch, useSort} from '../../lib/useQueryModifiers';
import Tooltip from '../tooltip';

export const Policies = props => {
  const {togglePortal, clicked} = usePortalContext();
  const [search, setSearch] = useSearch();
  const [sort, setSort] = useSort();
  const [pageNum, setPageNum] = useState(1);
  const [totalRecordsState, setTotalRecordsState] = useState(0);
  const [minPolicyState, setMinPolicyState] = useState(null);
  const showingDeleted = useAtomValue(showDeleted);
  const pageSize = useAtomValue(pageSizeRecoil);
  const {rca, hasPermission} = useCustomerContext();
  const minPolicyButtonText = rca ? "Define Minimum Policy" : "View Minimum Policy";

  // RBAC: POLICY_ASSIGNMENT_VIEW - "Hide the link to policy assignment page
  // (the link on the numbers in 'Assignments' column)". Computed here since
  // the 'group' column formatter below is a plain function, not a
  // component - hooks can't be called inside it.
  const canViewAssignments = hasPermission('POLICY_ASSIGNMENT_VIEW');

  const policiesFilters = useMemo(() => ({
    combineAnd: [
      ...search,
      ...sort,
      { field: "with.devices", value: `1` },
      { field: "with.policy_action", value: `1` },
      { field: "search.fields", value: `name` },
      { field: "options.page.num", value: `${pageNum}` },
      { field: "options.page.len", value: `${pageSize}` }
    ]
  }), [search, sort, pageNum, pageSize]);

  const {data, loading} = useQuery(POLICIES, 
    {
      variables: { filters: policiesFilters },
      skip: !sort.length
    });

  const {data: minPolicyData} = useQuery(MINIMUM_POLICY);
  useEffect(() => {
    minPolicyData && setMinPolicyState(minPolicyData?.MinimumPolicy?.result);
  }, [minPolicyData]);

  const totalQueried = data?.Policies?.metadata?.page?.records ?? totalRecordsState;
  
  useEffect(()=> {
    setTotalRecordsState(totalQueried);
  }, [totalQueried]);

  useEffect(() => {
    return setPageNum(1)
  }, [search]);

  let policies = data?.Policies?.result ?? [];
  policies = policies.map(policy => {
    return {
      ...policy,
      is_delete: showingDeleted,
      assignmentCount: policy.devices.length
    };
  });

  const columns = [
    { dataField: 'id', hidden: true },
    {
      dataField: 'name',
      text: 'POLICY NAME',
      formatter: (cell, row) => {
        const dvp = row?.is_default_visitor === true;
        const np = row?.is_compliant === false;
        return (
          <span className="d-flex align-items-center">
            {cell} 
            {dvp &&
              <Tooltip id={`as-${row.id}`} content={"Default Visitor Policy"}>
                <FontAwesomeIcon className="ms-3" icon={faUsersRectangle} size="lg" />
              </Tooltip>
            }
            {np &&
              <Tooltip id={`np-${row.id}`} content={"Noncompliant Policy"}>
                <FontAwesomeIcon className="ms-3" icon={faTriangleExclamation} size="2x" color="red" />
              </Tooltip>
            }
          </span>
        );
      }
    },
    {
      dataField: 'group',
      text: 'Assignments',
      align: 'left',
      formatter: (cell, row) => {
        if (!canViewAssignments) {
          return <>{row.assignmentCount || "0"}</>;
        }
        return (
          <>
            {row.assignmentCount ?
              <Link className="hdm-assignments-link" to={'/policy/assignments'} state={{policyId: row.id}} onClick={e => e.stopPropagation()}>
                {row.assignmentCount}
              </Link>
            : "0"}
          </>
        );
      }
    },
    {
      dataField: 'is_phone_protected',
      text: 'PROTECTED MODE ON',
      align: 'left',
      formatter: (cell, row) => (
        <div className="fa-lg me-4">
          <span className="fa-layers fa-fw">
            <FontAwesomeIcon icon={faCircle} size="2x" color={variables.privoroBlue} />
            <FontAwesomeIcon transform="shrink-1 right-6" icon={faCheck} color="white"/>
          </span>
        </div>
      )
    },
    {
      dataField: 'has_battery_warning',
      text: 'MIN BATTERY LEVEL',
      align: 'left',
      formatter: (cell, row) => (
        <>
        {cell ?
          <Badge pill className="policy-battery-warning-badge" variant={cell ? 'primary' : 'secondary'}>
            <FontAwesomeIcon size="lg" className="me-2" icon={cell ? faCheck : faXmark} color="white"/>
            {row.battery_warning_threshold}%
          </Badge>
        :
          <div className="fa-lg me-4">
            <span className="fa-layers fa-fw">
              <FontAwesomeIcon icon={faCircle} size="2x" color={variables.privoroOffGray} />
              <FontAwesomeIcon transform="shrink-1 right-6" icon={faXmark} color="white"/>
            </span>
          </div>
        }
        </>
      )
    },
    {
      dataField: 'is_cellular_connected',
      text: 'CELL RADIO OFF',
      align: 'left',
      formatter: (cell, row) => (
        <div className="fa-lg me-4">
          <span className="fa-layers fa-fw">
            <FontAwesomeIcon icon={faCircle} size="2x" color={cell ? variables.privoroBlue : variables.privoroOffGray} />
            <FontAwesomeIcon transform="shrink-1 right-6" icon={cell ? faCheck : faXmark} color="white"/>
          </span>
        </div>
      )
    },
    {
      dataField: 'modified_at',
      text: 'LAST UPDATED (UTC)',
      align: 'left',
      formatter: dateFormatter,
      sort: true
    }
  ];

  return (
    <>
      <Portal title={clicked === 'createpolicy' ? "Create Policy" : clicked === 'minpolicy' ? "Minimum Policy" : "Edit Policy"}>
        {clicked.length == "0" && <EditPolicy minPolicy={minPolicyState} title="Edit Policy" /> }
        {clicked === 'createpolicy' && <CreatePolicy minPolicy={minPolicyState} clicked={clicked} type="policy" title="Policy" />}
        {clicked === 'minpolicy' && <MinimumPolicy minPolicy={minPolicyState} clicked={clicked} type="policy" title={minPolicyButtonText} />}
      </Portal>
      <ActionBar showHidden={false} searchChange={setSearch} title="Manage Policies">
        <div><Button disabled={!hasPermission('POLICY_CREATE')} clicked={"createpolicy"} onClick={togglePortal}>Create Policy</Button></div>
        {/* No permission code exists for Minimum Policy in the RBAC sheet -
            this is gated by `rca` (root customer admin), a separate,
            non-RBAC concept. Left untouched. */}
        <div><Button clicked={"minpolicy"} onClick={togglePortal}>{minPolicyButtonText}</Button></div>
      </ActionBar>
      <Table
        testId="policy-table"
        columns={columns}
        data={policies}
        loading={loading}
        pageSize={pageSize}
        pageNum={pageNum}
        setPageNum={setPageNum}
        totalRecords={totalRecordsState}
        setSort={setSort}
      />
    </>
  );
};

const POLICIES = gql`
  query getPolicies($filters: GenericFilters!) {
    Policies (filters: $filters) {
      result {
        id name description is_default_visitor is_active is_delete is_phone_protected
        is_offline has_battery_warning battery_warning_threshold is_checked_in
        is_camera_exposed is_cellular_connected is_microphone_exposed is_phone_present
        is_compliant protected_profile is_phone_mismatch policy_action_id
        policy_action {
          antisurveillance_mdm_action antisurveillance_edge_action cellular_mdm_action
          cellular_edge_action low_battery_mdm_action low_battery_edge_action
        }
        created_at modified_at
        devices { id }
      }
      metadata { page { num len total inPage records } }
    }
  }
`;

export const MINIMUM_POLICY = gql`
  query getMinimumPolicy {
    MinimumPolicy {
      result {
        id
        is_phone_protected
        has_battery_warning
        battery_warning_threshold
        is_cellular_connected
      }
    }
  }
`;

export default Policies;
